/** Canonical e-sign role values and user-facing role labels. */

export const ESIGN_ROLE_LABELS = {
  client: 'Client',
  referral_attorney: 'Referral Attorney',
  handling_attorney: 'Attorney',
};

export const ESIGN_ROLE_ORDER = ['client', 'referral_attorney', 'handling_attorney'];

/** Default 2-signer docs when the API has not resolved required roles yet. */
export const DEFAULT_REQUIRED_SIGNER_ROLES = ['client', 'handling_attorney'];

/** Manual-entry fallback when suggested recipients cannot be resolved. */
export const FALLBACK_REQUIRED_SIGNER_ROLES = [...ESIGN_ROLE_ORDER];

const ROLE_ALIASES = {
  atty: 'handling_attorney',
  attorney: 'handling_attorney',
  'handling attorney': 'handling_attorney',
  'handling atty': 'handling_attorney',
  'ref atty': 'referral_attorney',
  'ref attorney': 'referral_attorney',
  'referral atty': 'referral_attorney',
};

export const normalizeESignRole = (rawRole) => {
  const raw = String(rawRole || '').trim();
  if (!raw) return '';

  for (const role of ESIGN_ROLE_ORDER) {
    if (raw === role || raw.toLowerCase() === role.toLowerCase()) return role;
    const label = ESIGN_ROLE_LABELS[role];
    if (raw === label || raw.toLowerCase() === label.toLowerCase()) return role;
  }

  return ROLE_ALIASES[raw.toLowerCase()] || '';
};

export const roleSortKey = (role) => {
  const index = ESIGN_ROLE_ORDER.indexOf(role);
  return index >= 0 ? index : ESIGN_ROLE_ORDER.length;
};

export const missingRoleLabel = (entry) =>
  ESIGN_ROLE_LABELS[entry?.role] || entry?.label || entry?.role || '';

export const collectRequiredSignerRoles = (signers = [], missingRoles = []) => {
  const roles = new Set();
  for (const signer of signers) {
    const canonical = normalizeESignRole(signer?.role);
    if (canonical) roles.add(canonical);
  }
  for (const entry of missingRoles) {
    const canonical = normalizeESignRole(entry?.role);
    if (canonical) roles.add(canonical);
  }
  return [...roles].sort((a, b) => roleSortKey(a) - roleSortKey(b));
};

export const buildRoleSelectOptions = (requiredRoles = []) =>
  [...requiredRoles]
    .sort((a, b) => roleSortKey(a) - roleSortKey(b))
    .map((role) => ({
      value: role,
      label: ESIGN_ROLE_LABELS[role] || role,
    }));

export const recipientSatisfiesRole = (recipient, requiredRole) => {
  if (!recipient?.name?.trim() || !recipient?.email?.trim() || !recipient?.role?.trim()) {
    return false;
  }
  return normalizeESignRole(recipient.role) === requiredRole;
};

/** Drop API-reported missing roles once a recipient fills that role. */
export const computeEffectiveMissingRoles = (initialMissingRoles = [], recipients = []) =>
  (initialMissingRoles || []).filter(
    (entry) => !recipients.some((recipient) => recipientSatisfiesRole(recipient, entry.role))
  );

export const STORED_SIGNATURE = 'stored';

/** "Adam G. Werner" and "Adam Werner" are the same person; middle initials are ignored. */
const personKey = (name) => {
  const tokens = String(name || '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 1);
  return tokens.length ? `${tokens[0]} ${tokens[tokens.length - 1]}` : '';
};

export const storedSignatureNameFor = (name, storedNames = []) => {
  const key = personKey(name);
  if (!key) return '';
  return (storedNames || []).find((stored) => personKey(stored) === key) || '';
};

export const canUseStoredSignature = (recipient, storedNames = []) =>
  normalizeESignRole(recipient?.role) === 'handling_attorney'
  && Boolean(storedSignatureNameFor(recipient?.name, storedNames));

/** Send the stored-signature choice only while the recipient still qualifies for it. */
export const applyStoredSignatureChoice = (signers = [], storedNames = []) =>
  signers.map(({ signature_method: method, ...signer }) =>
    method === STORED_SIGNATURE && canUseStoredSignature(signer, storedNames)
      ? { ...signer, signature_method: STORED_SIGNATURE }
      : signer
  );

export const normalizeSignerRolesForSubmit = (signers = []) =>
  signers.map((signer) => {
    const raw = String(signer.role || '').trim();
    if (!raw) return signer;
    const canonical = normalizeESignRole(raw);
    return canonical ? { ...signer, role: canonical } : signer;
  });
